import { Avatar, IconButton, TextField } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Clear } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import { AutocompleteInputChangeReason } from "@mui/material/useAutocomplete";
import { ICellEditorParams } from "ag-grid-community";
import { ICellEditorReactComp } from "ag-grid-react";
import { debounce } from "lodash";
import { ListboxProps } from "types/ListboxComponent.types";
import {
  Children,
  cloneElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FixedSizeList as VList, ListChildComponentProps } from "react-window";
import { useCCSE } from "../hooks";

const useStyles = makeStyles((theme) => ({
  divider: {
    height: 32,
    margin: "0 8px",
  },
  inputRoot: { flexWrap: "nowrap", paddingRight: "4px !important" },
  searchBar: {
    "& fieldset": {
      borderRadius: "20px",
    },
  },
  groupLabel: {
    fontWeight: 700,
    borderTop: "1px solid #E7E7E7",
  },
  option: {
    fontSize: 14,
    color: theme.palette.primary.main,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    overflow: "hidden",
    textOverflow: "ellipsis",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#eee",
    },
  },
  root: {
    minWidth: 320,
  },
}));

interface SearchOption {
  name: string;
  value: string;
  subText?: string;
  active: boolean;
}

let lastQuery: any;

const CCSECellEditor = forwardRef<ICellEditorReactComp, ICellEditorParams>(
  ({ value: initValue, api }, ref) => {
    const classes = useStyles();
    const [inputValue, setInputValue] = useState("");
    const [query, setQuery] = useState("");
    const [value, setValue] = useState<SearchOption | null>(null);
    const [options, setOptions] = useState<SearchOption[]>([]);
    const rootRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => ({
      isPopup: () => true,
      getValue: () => value?.name ?? null,
    }));

    const { data: users, error } = useCCSE();

    const generateOption = (
      users?: {
        ccseId: string;
        ccseName: string;
      }[]
    ): SearchOption[] => {
      if (users?.length) {
        return users.map((x) => {
          return {
            name: x.ccseName,
            value: x.ccseId,
            subText: x.ccseId,
            active: true,
          };
        });
      }
      return [];
    };

    const ops = useMemo(() => generateOption(users), [users]);

    useEffect(() => {
      setOptions(ops);
      if (ops.length && initValue) {
        const selected = ops.find((x) => x.name === initValue);
        if (selected) {
          setValue(selected);
        }
      }
    }, [ops, initValue]);

    const inputRef = useRef<typeof TextField>();

    const onChange = (_e: any, value: SearchOption | null) => {
      if (value) {
        setValue(value);
      }
      setTimeout(() => api?.stopEditing());
    };

    const onInputChange = (_e: any, input: string, reason: AutocompleteInputChangeReason) => {
      if (reason === "reset") return;
      setInputValue(input);
    };

    useEffect(() => {
      const debouncedSetQuery = debounce(() => {
        setQuery(inputValue);
      }, 500);
      if (lastQuery && lastQuery.cancel) {
        lastQuery.cancel();
      }
      lastQuery = debouncedSetQuery;
      debouncedSetQuery();
    }, [inputValue]);

    const clear = () => {
      setInputValue("");
    };

    useEffect(() => {
      //@ts-ignore
      setTimeout(() => inputRef?.current?.focus());
    }, []);

    // Use react-window for virtualization
    const ListboxComponent = forwardRef<HTMLDivElement, ListboxProps>(({ children }, ref) => {
      const itemData = Children.toArray(children);

      const Row = ({ index, style }: ListChildComponentProps) => {
        const item = itemData[index];
        return cloneElement(item as any, { style });
      };

      return (
        <div
          style={{ height: 336 }}
          ref={ref}
        >
          <VList
            height={336}
            width={320}
            itemCount={itemData.length}
            itemSize={42}
          >
            {Row}
          </VList>
        </div>
      );
    });

    if (error) return <span style={{ padding: 8, fontSize: 14 }}>Failed to load CSE options</span>;

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <Autocomplete
          ref={rootRef}
          classes={{
            option: classes.option,
            inputRoot: classes.inputRoot,
            root: classes.root,
          }}
          options={options}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          size="small"
          value={value}
          inputValue={inputValue}
          open={true}
          onChange={onChange}
          clearOnBlur={true}
          onInputChange={onInputChange}
          noOptionsText="No CSE found"
          loadingText="Please wait while we are fetching"
          loading={users === undefined}
          filterOptions={(options) =>
            options.filter(
              (o) =>
                !!!query ||
                o.name?.toLowerCase().includes(query.toLowerCase()) ||
                o.value?.toLowerCase().includes(query.toLowerCase())
            )
          }
          ListboxComponent={ListboxComponent as any}
          ListboxProps={{ ref: rootRef }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              variant="outlined"
              margin="dense"
              size="small"
              placeholder="CEC or Name"
              classes={{ root: classes.searchBar }}
              InputProps={{
                ...params.InputProps,
                style: { height: 28 },
                endAdornment: (
                  <>
                    {inputValue ? (
                      <IconButton
                        size="small"
                        onClick={clear}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    ) : null}
                  </>
                ),
              }}
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
          renderOption={(props, option, { inputValue }) => {
            const regex = new RegExp(inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig");
            const spanText = option.name.replace(
              regex,
              `<span style="background-color:#FFE399">$&</span>`
            );
            const subSpanText = option.subText
              ? option.subText.replace(regex, `<span style="background-color:#FFE399">$&</span>`)
              : "";
            return (
              <li {...props}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <Avatar
                    src={
                      option.value
                        ? `https://wwwin.cisco.com/dir/photo/zoom/${option.value}.jpg`
                        : undefined
                    }
                    style={{
                      width: 32,
                      height: 32,
                      marginRight: 8,
                    }}
                  >
                    {`${option.name?.[0]}`}
                  </Avatar>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          width: 320,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        dangerouslySetInnerHTML={{ __html: spanText }}
                      />
                    </div>
                    {subSpanText ? (
                      <div
                        style={{
                          color: "#666666",
                          fontSize: 12,
                        }}
                        dangerouslySetInnerHTML={{ __html: subSpanText }}
                      />
                    ) : null}
                  </div>
                </div>
              </li>
            );
          }}
        />
      </div>
    );
  }
);

export default CCSECellEditor;
